import { VmsDeviceStatus } from "@/types/vms";
import type { HybridDevice } from "@/types/vms";

/**
 * Operator-facing copy for a camera's VMS state.
 *
 * The VMS reports a per-camera `status` (see `VmsDeviceStatus`). Everything an
 * operator reads about that status is defined here so the widget badge, the
 * camera dialog, the fullscreen view and the player error state never drift
 * apart — and so none of them leak VMS jargon ("No VMS configuration found").
 */
export interface VmsCameraStatusCopy {
    /** Short badge label, e.g. "OFFLINE". */
    label: string;
    /** Plain-language explanation shown to the operator. */
    message: string;
}

/**
 * Statuses for which the VMS cannot serve video. Streaming one of these only
 * produces an endless spinner, so the camera is presented as unavailable up
 * front instead.
 */
const UNAVAILABLE_COPY: Record<string, VmsCameraStatusCopy> = {
    [VmsDeviceStatus.OFFLINE]: {
        label: "OFFLINE",
        message: "Camera is offline",
    },
    [VmsDeviceStatus.ERROR]: {
        label: "FAULT",
        message: "The video system reports a fault on this camera",
    },
    [VmsDeviceStatus.UNAUTHORIZED]: {
        label: "NO ACCESS",
        message: "The video system refused access to this camera",
    },
    [VmsDeviceStatus.INCOMPATIBLE]: {
        label: "UNSUPPORTED",
        message: "The video system does not support this camera",
    },
    [VmsDeviceStatus.UNCONFIGURED]: {
        label: "NOT SET UP",
        message: "This camera is not set up in the video system",
    },
};

/** Statuses that mean video should play. */
const AVAILABLE_COPY: Record<string, VmsCameraStatusCopy> = {
    [VmsDeviceStatus.ONLINE]: {
        label: "ONLINE",
        message: "Camera is online",
    },
    [VmsDeviceStatus.RECORDING]: {
        label: "RECORDING",
        message: "Camera is online and recording",
    },
};

/**
 * The VMS knows the camera but reports "undefined"/"unknown"/nothing at all.
 * We stay optimistic there: hiding a camera that would have streamed is worse
 * for an operator than a stream that fails.
 */
const UNKNOWN_STATUS_COPY: VmsCameraStatusCopy = {
    label: "AVAILABLE",
    message: "Camera status not reported by the video system",
};

/** No VMS record matched this camera at all — nothing to stream. */
const NOT_LINKED_COPY: VmsCameraStatusCopy = {
    label: "NO VIDEO",
    message: "No live video is available for this camera",
};

/** The VMS has not answered yet, so nothing is known about this camera. */
const PENDING_COPY: VmsCameraStatusCopy = {
    label: "LOADING",
    message: "Checking the video system for this camera",
};

/** Shown when no VMS device matched the camera at all. */
export const VMS_NO_VIDEO_MESSAGE = NOT_LINKED_COPY.message;

/** Statuses arrive as free-form strings from the API; compare defensively. */
function statusKey(status?: VmsDeviceStatus | string): string {
    return String(status ?? "").toLowerCase();
}

/**
 * Whether it is worth attempting a stream for a camera the VMS knows about.
 * Only known-bad statuses block; anything unrecognised is attempted.
 */
export function isVmsStatusStreamable(
    status?: VmsDeviceStatus | string,
): boolean {
    return !(statusKey(status) in UNAVAILABLE_COPY);
}

/** Plain-language reason a known-but-unstreamable camera has no video. */
export function vmsStatusUnavailableMessage(
    status?: VmsDeviceStatus | string,
): string {
    return (
        UNAVAILABLE_COPY[statusKey(status)]?.message ?? NOT_LINKED_COPY.message
    );
}

/** Badge label and explanation for a camera as rendered in the UI. */
export function vmsCameraStatusCopy(
    vms: HybridDevice["vms"],
): VmsCameraStatusCopy {
    const key = statusKey(vms.status);

    if (vms.pending) return PENDING_COPY;

    if (vms.available) {
        return AVAILABLE_COPY[key] ?? UNKNOWN_STATUS_COPY;
    }

    return (
        UNAVAILABLE_COPY[key] ?? {
            ...NOT_LINKED_COPY,
            message: vms.reason || NOT_LINKED_COPY.message,
        }
    );
}
